import { useState } from "react";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { EmptyState } from "@/components/EmptyState";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { call, CALLABLES } from "@/services/callables";
import { formatDateTime } from "@/lib/format";
import { useSupportRequests } from "./useSupportRequests";
import { minutesRemaining, stateOf, STATE_KEY, STATE_TONE } from "./lifecycle";

/**
 * Deciding whether to let Spontar look.
 *
 * The page belongs to the institution, not to us: it opens by saying that
 * nobody can look without being let in, lists what has been asked for and what
 * was decided, and offers a way to close an open window early.
 *
 * Refusals are kept as well as approvals. The record of access that was refused
 * is part of the same evidence as the record of access that was allowed.
 */
export function SupportPage() {
  const t = useT();
  const { locale } = useI18n();
  const { success, showError } = useToast();
  const { requests, loading } = useSupportRequests();
  const [busy, setBusy] = useState<string | undefined>(undefined);

  const now = Date.now();

  const decide = async (requestId: string, approve: boolean) => {
    setBusy(requestId);
    try {
      await call(CALLABLES.decideSupportAccess, { requestId, approve });
      success(approve ? "support.approved" : "support.denied");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(undefined);
    }
  };

  const revoke = async (requestId: string) => {
    setBusy(requestId);
    try {
      await call(CALLABLES.revokeSupportAccess, { requestId });
      success("support.revoked");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(undefined);
    }
  };

  return (
    <SectionShell titleKey="support.title">
      <p className="max-w-2xl text-ink-soft">{t("support.intro")}</p>
      <p className="mt-2 max-w-2xl text-sm text-ink-muted">{t("support.readOnly")}</p>

      <div className="mt-8">
        {loading ? (
          <p className="text-sm text-ink-muted">{t("common.loading")}</p>
        ) : requests.length === 0 ? (
          <EmptyState titleKey="support.empty" />
        ) : (
          <ul className="flex flex-col gap-4">
            {requests.map((request) => {
              const state = stateOf(request, now);
              return (
                <li key={request.id} className="card p-5">
                  <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <p className="text-sm text-ink">{request.requestedByEmail}</p>
                    <p className={`text-sm ${STATE_TONE[state]}`}>{t(STATE_KEY[state])}</p>
                  </div>

                  <p className="mt-3 text-ink-soft">{request.reason}</p>

                  <p className="mt-2 text-sm text-ink-muted">
                    {t("support.forHours", {
                      duration: t(`support.duration.${request.durationHours}`),
                    })}
                    <span className="ml-3">{formatDateTime(locale, request.createdAt)}</span>
                    {state === "approved" ? (
                      <span className="ml-3 text-positive">
                        {t("support.remaining", { count: minutesRemaining(request, now) })}
                      </span>
                    ) : null}
                  </p>

                  {state === "pending" ? (
                    <div className="mt-4 flex gap-2">
                      <Button busy={busy === request.id} onClick={() => decide(request.id, true)}>
                        {t("support.approve")}
                      </Button>
                      <Button
                        variant="secondary"
                        busy={busy === request.id}
                        onClick={() => decide(request.id, false)}
                      >
                        {t("support.deny")}
                      </Button>
                    </div>
                  ) : null}

                  {state === "approved" ? (
                    <div className="mt-4">
                      <Button
                        variant="danger"
                        busy={busy === request.id}
                        onClick={() => revoke(request.id)}
                      >
                        {t("support.revoke")}
                      </Button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </SectionShell>
  );
}
